/**
 * PersonnelHeader — header row for the Personnel on Duty card.
 *
 * Contains:
 *   - Section title with the 👮 icon
 *   - Add Supervisor button (with role picker)
 *   - Reset Shift button (with confirm dialog)
 *   - Desk Officer call sign input
 */
import { useState } from 'react'
import { useDesktopStore } from '../../stores/desktop'
import { markDirty } from '../../stores/sync'

export default function PersonnelHeader() {
  const { deskOfficer, setDeskOfficer, resetShift, addSupervisorSlot, toggleFcpd, fcpdOnDuty } = useDesktopStore()
  const [showSupPicker, setShowSupPicker] = useState(false)

  function handleReset() {
    if (confirm('Reset entire shift? This will clear all personnel, equipment, and notes.')) {
      resetShift()
    }
  }

  function handleAddSup(role: 'Dir/AD/Lt' | 'Sergeant' | 'OIC') {
    addSupervisorSlot(role)
    setShowSupPicker(false)
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        paddingBottom: 10,
        borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <span
        style={{
          color: 'var(--accent)',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}
      >
        👮 Personnel on Duty
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>

        {/* Add Supervisor */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowSupPicker(p => !p)}
            title="Add a supervisor slot"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              background: 'rgba(155,89,182,0.12)',
              color: '#c084fc',
              border: '1px solid rgba(155,89,182,0.35)',
              borderRadius: 8,
            }}
          >
            + Supervisor
          </button>
          {showSupPicker && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                zIndex: 50,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                minWidth: 160,
                overflow: 'hidden',
              }}
            >
              {(['Dir/AD/Lt', 'Sergeant', 'OIC'] as const).map(role => (
                <button
                  key={role}
                  onClick={() => handleAddSup(role)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  {role === 'Dir/AD/Lt' ? 'Director / AD / Lt' : role}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* FCPD Toggle — SC-81 (Radio 118) and SC-82 (Radio 108) */}
        {(['SC-81', 'SC-82'] as const).map(unit => {
          const isOn = fcpdOnDuty.includes(unit)
          const radio = unit === 'SC-81' ? '118' : '108'
          return (
            <button
              key={unit}
              onClick={() => toggleFcpd(unit)}
              title={isOn ? `Remove ${unit} from duty` : `Add ${unit} (Radio ${radio}) to duty`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                background: isOn ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)',
                color: isOn ? '#f87171' : '#60a5fa',
                border: `1px solid ${isOn ? 'rgba(239,68,68,0.35)' : 'rgba(59,130,246,0.35)'}`,
                borderRadius: 8,
              }}
            >
              {isOn ? `– ${unit}` : `+ ${unit}`}
              <span style={{ opacity: 0.7, fontWeight: 400 }}>R{radio}</span>
            </button>
          )
        })}

        <button
          onClick={handleReset}
          title="Reset entire shift"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            background: 'rgba(239,68,68,0.1)',
            color: '#ef4444',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.2)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
        >
          🔄 Reset Shift
        </button>

        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Desk Officer:</span>
        <input
          value={deskOfficer}
          onChange={e => {
            markDirty()
            setDeskOfficer(e.target.value)
          }}
          placeholder="Call sign"
          style={{
            background: 'var(--bg-deep)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            color: 'var(--accent)',
            fontSize: 12,
            padding: '5px 10px',
            outline: 'none',
            width: 100,
            fontWeight: 700,
          }}
        />
      </div>
    </div>
  )
}
